import axios from 'axios';
import type { AuthData, User } from '../types';

const AUTH_KEY = 'auth';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const getAuth = (): AuthData | null => {
  const authStr = localStorage.getItem(AUTH_KEY);
  if (!authStr) return null;
  try {
    return JSON.parse(authStr);
  } catch {
    return null;
  }
};

export const setAuth = (auth: AuthData): void => {
  localStorage.setItem(AUTH_KEY, JSON.stringify(auth));
};

export const clearAuth = (): void => {
  localStorage.removeItem(AUTH_KEY);
};

export const getUser = (): User | null => {
  const auth = getAuth();
  return auth?.user || null;
};

export const setUser = (user: User): void => {
  const auth = getAuth();
  if (auth) {
    auth.user = user;
    setAuth(auth);
  }
};

export const isAuthenticated = (): boolean => {
  return getAuth() !== null;
};

export const getAccessToken = (): string | null => {
  const auth = getAuth();
  return auth?.access_token || null;
};

export const getRefreshToken = (): string | null => {
  const auth = getAuth();
  return auth?.refresh_token || null;
};

export const updateTokens = (accessToken: string, refreshToken: string, user?: AuthData['user']): void => {
  const auth = getAuth();
  if (auth) {
    auth.access_token = accessToken;
    auth.refresh_token = refreshToken;
    if (user) {
      auth.user = user;
    }
    setAuth(auth);
  }
};

export const hasRole = (roles: string[]): boolean => {
  const user = getUser();
  if (!user) return false;

  // Check if any of the user's role system keys match the required roles
  if (user.roleSystemKeys?.length) {
    return user.roleSystemKeys.some(key => roles.includes(key));
  }

  // Fallback to roles array
  if (user.roles?.length) {
    return user.roles.some(role => roles.includes(role.systemKey || ''));
  }

  return false;
};

// Password Reset API Methods
export const requestPasswordReset = async (email: string): Promise<{ message: string }> => {
  const response = await axios.post<{ message: string }>(
    `${API_URL}/auth/forgot-password?app=plateforme`,
    { email }
  );
  return response.data;
};

export const validateResetToken = async (token: string): Promise<{ valid: boolean; email?: string }> => {
  const response = await axios.get<{ valid: boolean; email?: string }>(
    `${API_URL}/auth/reset-password/validate/${token}`
  );
  return response.data;
};

export const resetPassword = async (token: string, password: string): Promise<{ message: string }> => {
  const response = await axios.post<{ message: string }>(
    `${API_URL}/auth/reset-password`,
    { token, password }
  );
  return response.data;
};
